import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

/**
 * A stored image or document belonging to one verification step, as a presigned URL.
 *
 * `kind` is what lets the gallery caption things honestly:
 *   capture   — the selfie the applicant took just now
 *   photo     — a photo on record (off the Aadhaar)
 *   reference — the photo a Face Match compared against
 *   document  — a scan or an uploaded file
 */
export const stepArtefactSchema = z.object({
  kind: z.string(),
  label: z.string(),
  url: z.string(),
  isPdf: z.boolean(),
});

export type StepArtefact = z.infer<typeof stepArtefactSchema>;

const responseSchema = z.object({ items: z.array(stepArtefactSchema) });

/**
 * The step's stored artefacts, fetched only when its accordion is OPEN.
 *
 * Lazy on purpose. A five-step run would otherwise issue five requests and mint presigned
 * URLs for every applicant photo in it the moment the page loaded — including the steps
 * nobody scrolled to. These are face photos and identity documents, so a URL that was never
 * needed is pure exposure with no benefit.
 *
 * `staleTime` sits under the backend's 10-minute URL lifetime, so a cached entry cannot hand
 * back a link that has already expired. This is also why the admin path is the only one
 * wired: the artefact list is a reviewer's view of the whole step, not something an applicant
 * mid-run has any reason to fetch.
 */
export function useStepArtefacts(sessionId: string | undefined, stepId: string, enabled: boolean) {
  const tenantId = useTenantStore((s) => s.tenantId);

  return useQuery({
    queryKey: ['step-artefacts', tenantId ?? '', sessionId ?? '', stepId],
    queryFn: async ({ signal }) => {
      const raw = await apiClient
        .get(`api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/artefacts`, { signal })
        .json();
      return responseSchema.parse(raw).items;
    },
    enabled: enabled && !!tenantId && !!sessionId,
    // Comfortably inside the presigned URL's 10-minute validity.
    staleTime: 5 * 60 * 1000,
    // A step with nothing stored is the common case, not an error worth retrying.
    retry: false,
  });
}
